import { Interval } from '@nestjs/schedule';
import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { LessThanOrEqual, QueryFailedError, Repository } from 'typeorm';

import { Loggable } from '@ip6m/server/shared';

import {
  OtpAlreadyExistsForUserException,
  OtpIncompleteException,
  OtpNotFoundException,
} from '../exceptions';
import { OtpEntity } from '../models/otp-entity';

@Injectable()
export class OtpDataService {
  constructor(
    @InjectRepository(OtpEntity)
    private otpRepo: Repository<OtpEntity>
  ) {}

  static mapRepoError(otp) {
    return (error) => {
      if (error instanceof QueryFailedError) {
        if (/.*doesn't have a default value.*/.test(error.message)) {
          throw new OtpIncompleteException(error.message);
        }

        if (/.*Duplicate entry.*for key.*/.test(error.message)) {
          throw new OtpAlreadyExistsForUserException(otp.userId);
        }
      }

      throw error;
    };
  }

  /**
   * @throws OtpAlreadyExistsForUserException
   * @throws OtpIncompleteException
   */
  @Loggable
  async create(otp: Omit<OtpEntity, 'id'>): Promise<OtpEntity> {
    if (!otp.validUntil) {
      throw new OtpIncompleteException('OTP.validUntil is empty');
    }

    const existingOtp = await this.otpRepo
      .findOne({ userId: otp.userId })
      .catch(OtpDataService.mapRepoError(otp));
    if (existingOtp) {
      throw new OtpAlreadyExistsForUserException(otp.userId);
    }

    return this.otpRepo
      .save({ ...otp })
      .catch(OtpDataService.mapRepoError(otp));
  }

  /**
   * @throws OtpNotFoundException
   */
  @Loggable
  get(id: number): Promise<OtpEntity> {
    return this.otpRepo.findOneOrFail({ where: { id } }).catch(() => {
      throw new OtpNotFoundException(id);
    });
  }

  /**
   * @throws OtpNotFoundException
   */
  @Loggable
  async update(id: number, partialOtp: Partial<OtpEntity>): Promise<OtpEntity> {
    const otp = await this.get(id);
    for (const property of Object.keys(partialOtp)) {
      otp[property] = partialOtp[property];
    }

    return this.otpRepo
      .save(otp)
      .catch(OtpDataService.mapRepoError(partialOtp));
  }

  /**
   * @throws OtpNotFoundException
   */
  @Loggable
  async delete(id: number) {
    await this.get(id);
    await this.otpRepo.delete({ id });
  }

  @Loggable
  findByUserId(userId: number) {
    return this.otpRepo.find({ where: { userId } });
  }

  @Interval(60 * 60 * 1000)
  @Loggable
  async scheduledCleanup() {
    const expiredOtpList = await this.otpRepo.find({
      where: { validUntil: LessThanOrEqual(new Date()) },
    });
    for (const expiredOtp of expiredOtpList) {
      await this.delete(expiredOtp.id);
    }
  }
}
